import React, { useState, useEffect } from 'react';
import VariantSelectors from './VariantSelectors';

const isPromoActive = (product) => {
  if (!product.promoPriceCents) return false;
  const now = new Date();
  if (product.promoStartDate) {
    const start = new Date(product.promoStartDate);
    if (now < start) return false;
  }
  if (product.promoEndDate) {
    const end = new Date(product.promoEndDate + 'T23:59:59');
    if (now > end) return false;
  }
  return true;
};

const getTimeRemaining = (endDate) => {
  const end = new Date(endDate + 'T23:59:59');
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
};

const CountdownTimer = ({ endDate, theme }) => {
  const [time, setTime] = useState(() => getTimeRemaining(endDate));

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = getTimeRemaining(endDate);
      if (!remaining) {
        clearInterval(interval);
        setTime(null);
      } else {
        setTime(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  if (!time) return null;

  const blocks = [
    { label: 'j', value: time.days },
    { label: 'h', value: time.hours },
    { label: 'm', value: time.minutes },
    { label: 's', value: time.seconds },
  ];

  return (
    <div className="flex items-center gap-1.5" data-testid="promo-countdown">
      <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div className="flex items-center gap-1">
        {blocks.map((b, i) => (
          <span key={i} className="inline-flex items-center">
            <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded min-w-[28px] text-center">
              {String(b.value).padStart(2, '0')}
            </span>
            <span className="text-xs font-medium ml-0.5" style={{ color: theme?.mutedText }}>{b.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const ProductInfo = ({
  product, category, shop, accentColor, formatPrice,
  effectivePrice, variantGroups, selectedVariants, onSelectVariant,
  onWhatsApp, onCall, onShare, theme
}) => {
  const hasPromo = isPromoActive(product);
  const discount = hasPromo && product.priceCents
    ? Math.round(((product.priceCents - product.promoPriceCents) / product.priceCents) * 100)
    : 0;

  // Use variant price if selected, otherwise use promo or regular price
  const hasVariantPrice = effectivePrice !== product.priceCents && effectivePrice !== null;

  return (
    <div className="space-y-5">
      {/* Category */}
      {category && (
        <span className="text-sm font-medium" style={{ color: theme?.mutedText }}>
          {category.name}
        </span>
      )}

      {/* Name */}
      <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: theme?.textColor }} data-testid="product-name">
        {product.name}
      </h1>

      {/* Price section */}
      <div className="space-y-2">
        {hasVariantPrice ? (
          <p className="text-2xl font-bold" style={{ color: accentColor }} data-testid="product-price">
            {formatPrice(effectivePrice)}
          </p>
        ) : hasPromo ? (
          <div className="space-y-2" data-testid="promo-price-section">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-bold text-red-500" data-testid="promo-current-price">
                {formatPrice(product.promoPriceCents)}
              </span>
              <span className="text-lg line-through" style={{ color: theme?.mutedText }} data-testid="promo-original-price">
                {formatPrice(product.priceCents)}
              </span>
              {discount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="promo-discount-badge">
                  -{discount}%
                </span>
              )}
            </div>
            {product.promoEndDate && (
              <CountdownTimer endDate={product.promoEndDate} theme={theme} />
            )}
          </div>
        ) : (
          effectivePrice && (
            <p className="text-2xl font-bold" style={{ color: accentColor }} data-testid="product-price">
              {formatPrice(effectivePrice)}
            </p>
          )
        )}
      </div>

      {/* Variants */}
      {variantGroups && variantGroups.length > 0 && (
        <VariantSelectors
          groups={variantGroups}
          selectedVariants={selectedVariants}
          onSelectVariant={onSelectVariant}
          accentColor={accentColor}
          theme={theme}
        />
      )}

      {/* Description */}
      {product.description && (
        <div>
          <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: theme?.mutedText }}>
            {product.description}
          </p>
        </div>
      )}

      {/* WhatsApp share */}
      <div className="pt-1">
        <button
          onClick={() => {
            const price = hasPromo ? formatPrice(product.promoPriceCents) : formatPrice(effectivePrice);
            const msg = `Regarde ce que j'ai trouve chez ${shop?.name} !\n${product.name}${price ? ` - ${price}` : ''}\n${window.location.href}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }}
          className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ backgroundColor: '#25D366', color: '#FFFFFF' }}
          data-testid="share-whatsapp-button"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Partager sur WhatsApp
        </button>
      </div>

      {/* Desktop contact buttons */}
      <div className="hidden lg:flex gap-3 pt-2">
        {shop?.whatsappNumber && (
          <button
            onClick={onWhatsApp}
            className="flex-1 py-3.5 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2"
            style={{ backgroundColor: '#25D366' }}
            data-testid="whatsapp-button"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </button>
        )}
        {shop?.phone && (
          <button
            onClick={onCall}
            className="flex-1 py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border"
            style={{ color: theme?.textColor, borderColor: theme?.borderColor }}
            data-testid="call-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Appeler
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;
